<template>
  <div class="admin-page">
    <main class="form-page">
      <!-- List view -->
      <template v-if="viewMode === 'list'">
        <div class="form-page__header">
          <h2 class="form-page__title">Workflow-Konfigurationen</h2>
          <p class="form-page__subtitle">Übersicht aller Konfigurationen. Zum Bearbeiten anklicken.</p>
        </div>

        <div v-if="listError" class="info-box info-box--yellow">
          <span class="info-box__icon">⚠️</span>
          <p>{{ listError }}</p>
        </div>

        <div v-if="listLoading" class="info-box info-box--blue">
          <span class="info-box__icon">⏳</span>
          <p>Lädt Konfigurationen…</p>
        </div>

        <div v-else class="config-grid">
          <div v-for="group in groupedWorkflows" :key="group.clientId" class="client-card">
            <div class="client-card__top">
              <div>
                <h3 class="client-card__client-id">{{ group.clientId }}</h3>
                <p class="client-card__domain">{{ group.domain || '–' }}</p>
              </div>
              <span v-if="assignedFlowsByClientId[group.clientId]" class="badge badge--green">Aktiv</span>
              <span v-else class="badge badge--gray">Nicht zugeordnet</span>
            </div>

            <div class="client-card__section">
              <div class="client-card__section-label">Pipelines</div>
              <div class="pipeline-chips">
                <button
                  v-for="wf in group.pipelines"
                  :key="wf.workflow_type"
                  class="pipeline-chip"
                  @click="openDetail(wf)"
                >
                  {{ wf.workflow_type }}
                </button>
              </div>
            </div>

            <div class="client-card__section client-card__section--flow">
              <div class="client-card__section-label">Flow-Zuordnung</div>
              <p class="client-card__assigned-to">
                Zugeordnet zu:
                <strong>{{ assignedFlowsByClientId[group.clientId]?.join(', ') || '–' }}</strong>
              </p>
              <div class="flow-assign-row">
                <select class="field__select" v-model="cardFlowSelection[group.clientId]">
                  <option value="">– Flow wählen –</option>
                  <option v-for="flow in FLOWS" :key="flow.key" :value="flow.key">{{ flow.label }}</option>
                </select>
                <button
                  class="secondary-btn"
                  :disabled="!cardFlowSelection[group.clientId] || cardFlowSaving[group.clientId]"
                  @click="assignFlowToClient(group.clientId)"
                >
                  {{ cardFlowSaving[group.clientId] ? 'Speichert…' : 'Zuordnen' }}
                </button>
              </div>
              <span v-if="cardFlowStatus[group.clientId]" class="flow-assignment-row__status">
                {{ cardFlowStatus[group.clientId] }}
              </span>
            </div>
          </div>

          <div class="config-card config-card--add" @click="openCreate">
            <span class="config-card--add__icon">+</span>
            <span>Neue Konfiguration</span>
          </div>
        </div>
      </template>

      <!-- Detail / create view -->
      <template v-else>
        <button class="toggle-json-btn back-to-list-btn" @click="backToList">← Zur Übersicht</button>

        <div v-if="errorMessage" class="info-box info-box--yellow">
          <span class="info-box__icon">⚠️</span>
          <p>{{ errorMessage }}</p>
        </div>

        <div v-if="statusMessage" class="info-box info-box--green">
          <span class="info-box__icon">✅</span>
          <p>{{ statusMessage }}</p>
        </div>

        <!-- Existing config: view / edit -->
        <template v-if="viewMode === 'detail'">
          <div class="form-page__header">
            <h2 class="form-page__title">{{ workflow.client_id }}</h2>
            <p class="form-page__subtitle">{{ workflow.workflow_type }}</p>
          </div>

          <div class="form-section">
            <div class="summary-section">
              <div class="summary-section__title">Zusammenfassung</div>
              <div class="summary-row">
                <span class="summary-row__label">Config ID</span>
                <span class="summary-row__value">{{ workflow.config_id }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-row__label">Domain</span>
                <span class="summary-row__value">{{ workflow.domain }}</span>
              </div>
              <div class="summary-row">
                <span class="summary-row__label">Dokumenttypen</span>
                <span class="summary-row__value">{{ (workflow.meta_data?.document_types || []).join(', ') }}</span>
              </div>
            </div>
          </div>

          <div class="form-section" v-if="categories.length > 1">
            <div class="section-title">Kategorie</div>
            <select
              class="field__select"
              :value="selectedCategory"
              @change="selectCategory($event.target.value)"
            >
              <option v-for="cat in categories" :key="cat" :value="cat">{{ cat }}</option>
            </select>
          </div>

          <div class="form-section">
            <div class="section-title">Extraktionsfelder (Kategorie-Merkmal / Beschreibung)</div>
            <DynamicFieldList v-model="fields" />
          </div>

          <div class="form-section">
            <button class="toggle-json-btn" @click="showRawConfig = !showRawConfig">
              {{ showRawConfig ? 'Restliche Konfiguration ausblenden' : 'Restliche Konfiguration anzeigen (nur lesend)' }}
            </button>
            <pre v-if="showRawConfig" class="raw-config">{{ prettyConfiguration }}</pre>
          </div>

          <button class="primary-btn" :disabled="saving" @click="saveWorkflow">
            {{ saving ? 'Speichert…' : 'Änderungen speichern' }}
          </button>
        </template>

        <!-- Create -->
        <template v-else-if="viewMode === 'create'">
          <div class="form-page__header">
            <h2 class="form-page__title">Neue Konfiguration anlegen</h2>
          </div>

          <div class="form-section">
            <div class="field-row field-row--2">
              <div class="field">
                <label class="field__label">Client ID<span class="required-mark">*</span></label>
                <input class="field__input" type="text" v-model="newClientId" />
              </div>
              <div class="field">
                <label class="field__label">Workflow-Typ<span class="required-mark">*</span></label>
                <select class="field__select" v-model="newWorkflowType">
                  <option v-for="wt in workflowApi.WORKFLOW_TYPES" :key="wt" :value="wt">{{ wt }}</option>
                </select>
              </div>
            </div>
            <div class="field-row field-row--2">
              <div class="field">
                <label class="field__label">Domain / Flow<span class="required-mark">*</span></label>
                <input class="field__input" type="text" v-model="newDomain" placeholder="z. B. FormIQ 2" />
              </div>
              <div class="field">
                <label class="field__label">Dokumenttyp<span class="required-mark">*</span></label>
                <input class="field__input" type="text" v-model="newDocumentType" />
              </div>
            </div>
          </div>

          <div class="form-section">
            <div class="section-title">Extraktionsfelder (Kategorie-Merkmal / Beschreibung)</div>
            <DynamicFieldList v-model="fields" />
          </div>

          <button class="primary-btn" :disabled="saving" @click="createWorkflow">
            {{ saving ? 'Erstellt…' : 'Konfiguration erstellen' }}
          </button>
        </template>
      </template>
    </main>
  </div>
</template>

<script>
import { ref, computed, onMounted } from 'vue';
import { workflowApi } from '../services/workflowApi.js';
import { flowAssignmentApi } from '../services/flowAssignmentApi.js';
import { extractFields, applyFields, buildTemplateConfiguration } from '../utils/extractionFields.js';
import { getCategories, getFieldsForCategory, setFieldsForCategory } from '../utils/categoryExtraction.js';
import DynamicFieldList from '../components/DynamicFieldList.vue';

const FLOWS = [
  { key: 'formiq1', label: 'FormIQ 1 (Literatur)', defaultClientId: 'form1_foerderantrag' },
  { key: 'formiq2', label: 'FormIQ 2 (Einbürgerung)', defaultClientId: 'form2_einbuergerung' },
  { key: 'formiq3', label: 'FormIQ 3 (Baumschutz)', defaultClientId: 'form3_baumschutz' },
];

export default {
  name: 'AdminWorkflowConfigPage',
  components: { DynamicFieldList },
  setup() {
    const viewMode = ref('list'); // 'list' | 'detail' | 'create'
    const workflows = ref([]);
    const listLoading = ref(false);
    const listError = ref('');

    const groupedWorkflows = computed(() => {
      const groups = new Map();
      workflows.value.forEach((wf) => {
        if (!groups.has(wf.client_id)) {
          groups.set(wf.client_id, { clientId: wf.client_id, domain: wf.domain, pipelines: [] });
        }
        groups.get(wf.client_id).pipelines.push(wf);
      });
      return [...groups.values()];
    });

    const flowAssignments = ref(Object.fromEntries(FLOWS.map((f) => [f.key, f.defaultClientId])));
    const cardFlowSelection = ref({});
    const cardFlowSaving = ref({});
    const cardFlowStatus = ref({});

    const assignedFlowsByClientId = computed(() => {
      const map = {};
      FLOWS.forEach((flow) => {
        const clientId = flowAssignments.value[flow.key];
        if (!clientId) return;
        (map[clientId] ||= []).push(flow.label);
      });
      return map;
    });

    async function fetchFlowAssignments() {
      for (const flow of FLOWS) {
        try {
          const assignment = await flowAssignmentApi.getFlowAssignment(flow.key);
          flowAssignments.value[flow.key] = assignment?.client_id || flow.defaultClientId;
        } catch {
          flowAssignments.value[flow.key] = flow.defaultClientId;
        }
      }
    }

    async function assignFlowToClient(clientId) {
      const flowKey = cardFlowSelection.value[clientId];
      if (!flowKey) return;
      cardFlowSaving.value[clientId] = true;
      cardFlowStatus.value[clientId] = '';
      try {
        await flowAssignmentApi.setFlowAssignment(flowKey, clientId);
        flowAssignments.value[flowKey] = clientId;
        cardFlowStatus.value[clientId] = 'Gespeichert.';
      } catch (err) {
        cardFlowStatus.value[clientId] = err.message;
      } finally {
        cardFlowSaving.value[clientId] = false;
      }
    }

    const saving = ref(false);
    const errorMessage = ref('');
    const statusMessage = ref('');

    const workflow = ref(null);
    const fields = ref([]);
    const categories = ref([]);
    const selectedCategory = ref('');
    const showRawConfig = ref(false);
    const prettyConfiguration = ref('');

    // Writes the fields currently being edited back onto the category they
    // belong to before switching categories/saving, so a multi-category
    // workflow (e.g. form3_baumschutz's DOCUMENT_CATEGORISE) doesn't lose
    // edits to categories other than the first when the admin switches
    // between them.
    function persistCurrentCategoryFields() {
      if (!selectedCategory.value || !workflow.value) return;
      workflow.value.configuration = setFieldsForCategory(
        workflow.value.configuration, selectedCategory.value, fields.value
      );
    }

    function selectCategory(documentType) {
      persistCurrentCategoryFields();
      selectedCategory.value = documentType;
      fields.value = getFieldsForCategory(workflow.value.configuration, documentType);
    }

    const newClientId = ref('');
    const newWorkflowType = ref('DOCUMENT_PROCESS');
    const newDomain = ref('');
    const newDocumentType = ref('');

    async function fetchList() {
      listLoading.value = true;
      listError.value = '';
      try {
        const clientIds = FLOWS.map((flow) => flow.defaultClientId);
        const results = await Promise.all(
          clientIds.flatMap((clientId) =>
            workflowApi.WORKFLOW_TYPES.map((workflowType) => workflowApi.getWorkflow(clientId, workflowType))
          )
        );
        workflows.value = results.filter(Boolean);
      } catch (err) {
        listError.value = err.message;
      } finally {
        listLoading.value = false;
      }
    }

    async function openDetail(item) {
      errorMessage.value = '';
      statusMessage.value = '';
      try {
        const result = await workflowApi.getWorkflow(item.client_id, item.workflow_type);
        if (!result) {
          errorMessage.value = 'Konfiguration nicht gefunden.';
          return;
        }
        workflow.value = result;
        categories.value = getCategories(result.configuration);
        selectedCategory.value = categories.value[0] || '';
        fields.value = selectedCategory.value
          ? getFieldsForCategory(result.configuration, selectedCategory.value)
          : extractFields(result.configuration);
        prettyConfiguration.value = JSON.stringify(result.configuration, null, 2);
        showRawConfig.value = false;
        viewMode.value = 'detail';
      } catch (err) {
        errorMessage.value = err.message;
      }
    }

    function openCreate() {
      errorMessage.value = '';
      statusMessage.value = '';
      newClientId.value = '';
      newWorkflowType.value = 'DOCUMENT_PROCESS';
      newDomain.value = '';
      newDocumentType.value = '';
      fields.value = [];
      viewMode.value = 'create';
    }

    function backToList() {
      viewMode.value = 'list';
      fetchList();
    }

    async function saveWorkflow() {
      saving.value = true;
      errorMessage.value = '';
      statusMessage.value = '';
      try {
        let configuration = workflow.value.configuration;
        if (selectedCategory.value) {
          persistCurrentCategoryFields();
          configuration = workflow.value.configuration;
        } else {
          configuration = applyFields(configuration, fields.value);
        }
        const payload = {
          client_id: workflow.value.client_id,
          workflow_type: workflow.value.workflow_type,
          domain: workflow.value.domain,
          meta_data: workflow.value.meta_data,
          configuration,
        };
        const updated = await workflowApi.updateWorkflow(payload);
        workflow.value = updated;
        categories.value = getCategories(updated.configuration);
        fields.value = selectedCategory.value
          ? getFieldsForCategory(updated.configuration, selectedCategory.value)
          : extractFields(updated.configuration);
        prettyConfiguration.value = JSON.stringify(updated.configuration, null, 2);
        statusMessage.value = 'Änderungen gespeichert.';
      } catch (err) {
        errorMessage.value = err.message;
      } finally {
        saving.value = false;
      }
    }

    async function createWorkflow() {
      saving.value = true;
      errorMessage.value = '';
      statusMessage.value = '';
      try {
        const configuration = applyFields(buildTemplateConfiguration(newDocumentType.value), fields.value);
        const payload = {
          client_id: newClientId.value,
          workflow_type: newWorkflowType.value,
          domain: newDomain.value,
          meta_data: { document_types: [newDocumentType.value] },
          configuration,
        };
        const created = await workflowApi.createWorkflow(payload);
        workflow.value = created;
        categories.value = getCategories(created.configuration);
        selectedCategory.value = categories.value[0] || '';
        fields.value = extractFields(created.configuration);
        prettyConfiguration.value = JSON.stringify(created.configuration, null, 2);
        showRawConfig.value = false;
        statusMessage.value = 'Konfiguration erstellt.';
        viewMode.value = 'detail';
      } catch (err) {
        errorMessage.value = err.message;
      } finally {
        saving.value = false;
      }
    }

    onMounted(() => {
      fetchList();
      fetchFlowAssignments();
    });

    return {
      workflowApi,
      FLOWS,
      viewMode,
      workflows,
      listLoading,
      listError,
      groupedWorkflows,
      flowAssignments,
      cardFlowSelection,
      cardFlowSaving,
      cardFlowStatus,
      assignedFlowsByClientId,
      assignFlowToClient,
      saving,
      errorMessage,
      statusMessage,
      workflow,
      fields,
      categories,
      selectedCategory,
      selectCategory,
      showRawConfig,
      prettyConfiguration,
      newClientId,
      newWorkflowType,
      newDomain,
      newDocumentType,
      openDetail,
      openCreate,
      backToList,
      saveWorkflow,
      createWorkflow,
    };
  },
};
</script>

<style scoped src="../forms/form.css"></style>
<style scoped>
.admin-page {
  background: #f0f2f8;
}

/* form.css's .form-page is tuned for the narrow citizen-facing application
   forms it's shared with (see ApplicationViewC.vue) - the admin page needs
   the full content width instead. */
.form-page {
  max-width: none;
  margin: 0;
}

.back-to-list-btn {
  display: inline-block;
  margin-bottom: 20px;
}

.primary-btn {
  margin-top: 16px;
  background: #003366;
  color: #fff;
  border: none;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s;
}

.primary-btn:hover {
  background: #002a52;
}

.primary-btn:disabled {
  background: #adb5bd;
  cursor: not-allowed;
}

.flow-assignment-row__status {
  font-size: 12px;
  color: #1a7a45;
}

.secondary-btn {
  background: #eef4fb;
  border: 1px solid #c3d9f0;
  color: #1e6eb5;
  padding: 8px 18px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;
}

.secondary-btn:hover {
  background: #dbe9f7;
}

.secondary-btn:disabled {
  background: #f1f5f9;
  color: #94a3b8;
  cursor: not-allowed;
}

.toggle-json-btn {
  background: none;
  border: none;
  color: #1e6eb5;
  font-size: 13px;
  cursor: pointer;
  padding: 0;
  text-decoration: underline;
}

.raw-config {
  margin-top: 12px;
  background: #f8f9fb;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  padding: 16px;
  font-size: 12px;
  color: #333;
  overflow-x: auto;
  max-height: 400px;
}

.config-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
  gap: 20px;
}

.config-card {
  background: #fff;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  transition: transform 0.15s, box-shadow 0.15s, border-color 0.15s;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.config-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
  border-color: #60a5fa;
}

.client-card {
  background: #fff;
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.06);
  transition: box-shadow 0.15s, border-color 0.15s;
  display: flex;
  flex-direction: column;
  gap: 14px;
}
.client-card:hover {
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.1);
}

.client-card__top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
}

.client-card__client-id {
  font-size: 16px;
  font-weight: 800;
  color: #1a1a2e;
}

.client-card__domain {
  font-size: 12px;
  color: #888;
  margin-top: 2px;
}

.client-card__section {
  padding-top: 12px;
  border-top: 1px solid #eef0f2;
}

.client-card__section-label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 8px;
}

.pipeline-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.pipeline-chip {
  background: #eef4fb;
  border: 1px solid #c3d9f0;
  color: #1e6eb5;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.15s, border-color 0.15s;
}
.pipeline-chip:hover {
  background: #1e6eb5;
  border-color: #1e6eb5;
  color: #fff;
}

.client-card__assigned-to {
  font-size: 12px;
  color: #555;
  margin-bottom: 8px;
}

.flow-assign-row {
  display: flex;
  gap: 8px;
}
.flow-assign-row .field__select {
  flex: 1;
  max-width: 220px;
}

.badge--gray {
  background: #eef0f2;
  color: #64748b;
}

.config-card--add {
  align-items: center;
  justify-content: center;
  color: #1e6eb5;
  border: 2px dashed #c3d9f0;
  background: #f7fafd;
  font-weight: 700;
  font-size: 14px;
  min-height: 140px;
}

.config-card--add:hover {
  background: #eef4fb;
  border-color: #1e6eb5;
}

.config-card--add__icon {
  font-size: 32px;
  line-height: 1;
}
</style>
